import { Link, useLocation } from 'react-router-dom';
import useSession from '../../hooks/useSession';
import ChevronLeftIcon from '@/assets/icons/chevron-left.svg?react';
import BarsIcon from '@/assets/icons/bars.svg?react';
import XmarkIcon from '@/assets/icons/xmark.svg?react';
import ChartBarIcon from '@/assets/icons/chart-bar.svg?react';
import UsersIcon from '@/assets/icons/users.svg?react';

import styles from './navbar.module.scss';
import { useEffect, useState } from 'react';
import { classnames } from '@/utils/functions';
import ThreadsList from './ThreadsList';
import Logo from '../Logo';
import UserOptions from './UserOptions';
import useResponsive from '@/hooks/useResponsive';
import Button from '../Button';
import { Role } from '@/models/user';
import { hasThreadsAccess } from '@/utils/session';

const NAVBAR_STORE_KEY = 'navbarCollapsed';

const Navbar = () => {
  const getCollapsedValueFromStore = () =>
    localStorage.getItem(NAVBAR_STORE_KEY) === 'true';

  const [collapsed, setCollapsed] = useState(getCollapsedValueFromStore());
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { session, pushUserEvent } = useSession();

  const toggleMobileMenu = () => {
    setShowMobileMenu((current) => !current);
  };
  const toggleCollapse = () => {
    setCollapsed((current) => {
      const newValue = !current;
      localStorage.setItem(NAVBAR_STORE_KEY, newValue ? 'true' : 'false');

      pushUserEvent(`${newValue ? 'collapse' : 'expand'}_navbar`);
      return newValue;
    });
  };

  useResponsive(
    () => {
      setCollapsed(false);
      setShowMobileMenu(false);
    },
    () => {
      setCollapsed(getCollapsedValueFromStore());
      setShowMobileMenu(false);
    }
  );

  const location = useLocation();

  useEffect(() => {
    setShowMobileMenu(false);
  }, [location]);

  return (
    <nav
      className={classnames(
        styles['navbar'],
        { [styles['collapsed']]: collapsed },
        { [styles['show-mobile-content']]: showMobileMenu }
      )}
    >
      <button
        data-testid="navbar-trigger"
        className={styles['navbar-trigger']}
        onClick={toggleMobileMenu}
        aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
        aria-expanded={showMobileMenu}
      >
        {showMobileMenu ? <XmarkIcon /> : <BarsIcon />}
      </button>
      <Link data-testid="home-cta" className={styles['logo-wrapper']} to="/">
        <Logo className={styles['logo']} label="Gannet home" />
      </Link>
      <div className={styles['navbar-content']}>
        <div className={styles['navbar-menu']}>
          {hasThreadsAccess(session.roles) && (
            <Link data-testid={'new-thread-btn'} to="/">
              <Button
                className={styles['new-thread-btn']}
                onClick={() => {
                  pushUserEvent('click_new_thread');
                }}
              >
                {collapsed ? '+' : 'New Thread'}
              </Button>
            </Link>
          )}
          <Link to="/explore-data">
            <Button
              variation="secondary"
              className={styles['icon-btn']}
              aria-label={collapsed ? 'Explore Data' : undefined}
              onClick={() => {
                pushUserEvent('click_explore');
              }}
            >
              {collapsed ? <ChartBarIcon aria-hidden="true" /> : 'Explore Data'}
            </Button>
          </Link>
          {session.roles.includes(Role.ADMIN) && (
            <Link to="/users-management">
              <Button
                variation="secondary"
                className={styles['icon-btn']}
                aria-label={collapsed ? 'Users Management' : undefined}
              >
                {collapsed ? (
                  <UsersIcon aria-hidden="true" />
                ) : (
                  'Users Management'
                )}
              </Button>
            </Link>
          )}
        </div>
        {!collapsed && hasThreadsAccess(session.roles) && (
          <>
            <span className={styles['threads-heading']}>
              All Threads ({session.threads.length})
              <Link
                to="search"
                onClick={() => {
                  pushUserEvent('click_search');
                }}
              >
                Search
              </Link>
            </span>

            <ThreadsList />
          </>
        )}
        <UserOptions collapsed={collapsed} />

        <button
          className={styles['collapse-trigger']}
          onClick={toggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
        >
          <ChevronLeftIcon />
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
